import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Home, Mail, Lock, User, Building2, Phone, MapPin, ArrowRight, LogIn } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RealtorAuthPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  
  // Login form
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // Register form
  const [name, setName] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [brokerage, setBrokerage] = useState("");
  const [phone, setPhone] = useState("");
  
  // Check if already logged in
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/realtor/auth/me`, {
        withCredentials: true
      });
      if (response.data?.realtor_id) {
        navigate("/realtor/dashboard");
      }
    } catch (error) {
      // Not logged in
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/realtor/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      toast.success("Login successful!");
      navigate("/realtor/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/realtor/auth/register`,
        {
          email,
          password,
          name,
          license_number: licenseNumber,
          brokerage,
          phone
        },
        { withCredentials: true }
      );
      
      toast.success("Registration successful!");
      navigate("/realtor/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-black via-[#0A0A0A] to-black items-center justify-center p-12">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-8">
            <Home className="w-10 h-10 text-gold" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-white uppercase mb-4">
            DealForge
          </h1>
          <h2 className="text-2xl text-gold font-heading uppercase mb-6">
            Realtor Portal
          </h2>
          <p className="text-white/50 text-lg mb-8">
            A transaction operating system designed to increase deal velocity,
            transparency, and transaction control.
          </p>
          
          <div className="grid grid-cols-2 gap-4 text-left">
            {[
              "Listing Management",
              "Offer Tracking",
              "Transaction Workflow",
              "Investor Integration",
              "Commission Tracking",
              "Performance Analytics"
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-2 text-white/70">
                <div className="w-2 h-2 bg-gold" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-16 h-16 bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-4">
              <Home className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white uppercase">
              Realtor Portal
            </h1>
          </div>

          {/* Toggle */}
          <div className="flex mb-8">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 text-center font-bold transition-all duration-300 ${
                isLogin 
                  ? "bg-gold text-black" 
                  : "bg-[#0A0A0A]/80 backdrop-blur-sm text-white/50 hover:text-white"
              }`}
            >
              Login
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 text-center font-bold transition-all duration-300 ${
                !isLogin 
                  ? "bg-gold text-black" 
                  : "bg-[#0A0A0A]/80 backdrop-blur-sm text-white/50 hover:text-white"
              }`}
            >
              Register
            </button>
          </div>

          {isLogin ? (
            /* Login Form */
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label className="text-white/50">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="agent@example.com"
                    required
                    className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-white/50">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full gold-gradient-btn rounded-none font-bold h-12"
              >
                {loading ? "Signing in..." : "Sign In"}
                <LogIn className="w-5 h-5 ml-2" />
              </Button>
            </form>
          ) : (
            /* Register Form */
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <Label className="text-white/50">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="John Smith"
                    required
                    className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-white/50">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="agent@example.com"
                    required
                    className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-white/50">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/50">License #</Label>
                  <Input
                    type="text"
                    value={licenseNumber}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                    placeholder="TX-123456"
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/50">Phone</Label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-white/50">Brokerage</Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    type="text"
                    value={brokerage}
                    onChange={(e) => setBrokerage(e.target.value)}
                    placeholder="Keller Williams Austin"
                    className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full gold-gradient-btn rounded-none font-bold h-12"
              >
                {loading ? "Creating Account..." : "Create Account"}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </form>
          )}

          {/* Back to main app */}
          <div className="mt-8 text-center">
            <Link to="/" className="text-white/50 hover:text-gold text-sm">
              ← Back to DealForge
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
